import React, { useState, useEffect } from "react";
import { Modal, Input, Select, Table, Dropdown, notification } from "antd";
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { LuX, LuSearch, LuFilter } from "react-icons/lu";
import CreateFormModal from "./CreateFormModal";
import { ACTIONS_DROPDOWN_PROPS } from "../../constants/actionsDropdown";
import { getStatusColor } from "../../utils";
import {
  useGetFormsQuery,
  useDeleteFormMutation,
  useUpdateCampaignMutation,
  useGetCampaignByIdQuery,
} from "../../store/api/apiSlice";

const formatStatusLabel = (status) => {
  if (status === "not_active") return "Inactive";
  if (!status) return "—";
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
};

const AddFormModal = ({ visible, onClose, campaignId, onSuccess }) => {
  const [selectedForms, setSelectedForms] = useState(new Set());
  const [createFormModalVisible, setCreateFormModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editingForm, setEditingForm] = useState(null);

  // API Hooks
  const { data: formsData, isLoading } = useGetFormsQuery();
  const { data: campaignData, refetch: refetchCampaign } =
    useGetCampaignByIdQuery(campaignId);
  const [deleteForm] = useDeleteFormMutation();
  const [updateCampaign] = useUpdateCampaignMutation();

  // Initialize selected forms from campaign data
  useEffect(() => {
    if (campaignData?.data?.forms) {
      const currentFormIds = new Set(
        campaignData.data.forms.map((form) => form._id)
      );
      setSelectedForms(currentFormIds);
    }
  }, [campaignData]);

  const handleDeleteForm = async (formId) => {
    try {
      await deleteForm(formId).unwrap();
      notification.success({
        message: "Success",
        description: "Form deleted successfully",
      });
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to delete form",
      });
    }
  };

  // Update the handleFormSelection function
  const handleFormSelection = async (formId) => {
    try {
      const newSelected = new Set(selectedForms);
      const isCurrentlySelected = newSelected.has(formId);

      if (isCurrentlySelected) {
        // Remove form from campaign - send just the updated form IDs
        const updatedFormIds = campaignData.data.forms
          .filter((f) => f._id !== formId)
          .map((f) => f._id);

        await updateCampaign({
          id: campaignId,
          data: { forms: updatedFormIds },
        }).unwrap();
        newSelected.delete(formId);
      } else {
        // Add form to campaign - send just the updated form IDs
        const updatedFormIds = [
          ...(campaignData.data.forms || []).map((f) => f._id),
          formId,
        ];

        await updateCampaign({
          id: campaignId,
          data: { forms: updatedFormIds },
        }).unwrap();
        newSelected.add(formId);
      }

      setSelectedForms(newSelected);
      refetchCampaign();
      notification.success({
        message: "Success",
        description: `Form ${
          isCurrentlySelected ? "removed from" : "added to"
        } campaign successfully`,
      });
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to update campaign forms",
      });
    }
  };

  const handleActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    if (actionKey === "edit") {
      setEditingForm(record);
      setCreateFormModalVisible(true);
      return;
    }

    if (actionKey === "delete") {
      handleDeleteForm(record._id);
      return;
    }

    if (actionKey === "add" || actionKey === "remove") {
      handleFormSelection(record._id);
    }
  };

  const getActionsMenuItems = (record) => {
    const isSelected = selectedForms.has(record._id);

    return [
      { key: "edit", label: "Edit", icon: <EditOutlined /> },
      isSelected
        ? { key: "remove", label: "Remove", icon: <MinusOutlined /> }
        : { key: "add", label: "Add", icon: <PlusOutlined /> },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        icon: <DeleteOutlined />,
      },
    ];
  };

  // Filter forms based on search, status, and organization
  const filteredForms = formsData?.data?.filter((form) => {
    const matchesSearch =
      !searchText ||
      form.title?.toLowerCase().includes(searchText.toLowerCase()) ||
      form.description?.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = !statusFilter || form.status === statusFilter;
    // Filter by organization - only show forms belonging to the same organization as the campaign
    const matchesOrganization = campaignData?.data?.organization?._id
      ? form.organization?._id === campaignData.data.organization._id
      : true;
    return matchesSearch && matchesStatus && matchesOrganization;
  });

  const columns = [
    {
      title: "Sr#",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Form",
      key: "form",
      render: (_, record) => (
        <div>
          <div className="font-medium text-gray-900">{record.title}</div>
          <div className="text-sm text-gray-500 truncate max-w-xl">
            {record.description}
          </div>
          <span
            className="inline-block px-3 py-1 bg-white text-sm rounded-full mt-1"
            style={{ border: "1px solid #000" }}
          >
            {record.type || "Form Type"}
          </span>
        </div>
      ),
    },
    {
      title: "Questions",
      render: (record) => (
        <span>Questions {record.questions?.length || 0}</span>
      ),
    },
    {
      title: "Responses",
      render: (record) => (
        <span>
          {record.responses?.length || 0} ({record.responseRate || "0%"})
        </span>
      ),
    },
    {
      title: "Status",
      render: (_, record) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm capitalize ${getStatusColor(
            record.status
          )}`}
        >
          {formatStatusLabel(record.status)}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <div
          className="flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            {...ACTIONS_DROPDOWN_PROPS}
            menu={{
              items: getActionsMenuItems(record),
              onClick: (event) =>
                handleActionMenuClick(record, event.key, event),
            }}
          >
            <button
              type="button"
              className="actions-dots-btn"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Open actions for ${record.title || "form"}`}
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];
  return (
    <Modal
      closeIcon={
        <div className="bg-secondary mt-2 flex h-8 w-8 items-center justify-center rounded-full">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      centered
      title={
        <div className="relative -top-4 flex items-center justify-between">
          <h3>Add Form</h3>
          <button
            type="button"
            onClick={() => setCreateFormModalVisible(true)}
            className="mr-8 rounded-xl bg-[#18181B] px-6 py-2.5 text-sm font-medium text-white"
          >
            Create Form
          </button>
        </div>
      }
      className="add-form-modal [&_.ant-modal]:overflow-hidden [&_.ant-modal-content]:overflow-hidden"
      styles={{ body: { overflow: "hidden" } }}
    >
      <div className="flex max-h-[calc(100vh-140px)] flex-col">
        <div className="mb-6 flex shrink-0 items-center gap-4">
          <Input
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<LuSearch className="text-gray-400" />}
            suffix={
              searchText ? (
                <LuX
                  className="cursor-pointer text-gray-400"
                  onClick={() => setSearchText("")}
                />
              ) : null
            }
            className="h-11 flex-1 rounded-xl"
          />
          <Select
            placeholder="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            className="h-11 w-32 rounded-xl"
            options={[
              { value: "", label: "All Status" },
              { value: "active", label: "Active" },
              { value: "draft", label: "Draft" },
              { value: "not_active", label: "Inactive" },
            ]}
          />
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-xl border"
          >
            <LuFilter className="text-gray-600" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <Table
            columns={columns}
            dataSource={filteredForms}
            pagination={false}
            scroll={{ x: true }}
            loading={isLoading}
            rowKey="_id"
            className="forms-table"
          />
        </div>
      </div>

      <CreateFormModal
        visible={createFormModalVisible}
        onClose={() => {
          setCreateFormModalVisible(false);
          setEditingForm(null);
        }}
        editData={editingForm}
        onSuccess={() => {
          setCreateFormModalVisible(false);
          setEditingForm(null);
        }}
      />
    </Modal>
  );
};

export default AddFormModal;
